import * as React from 'react';
import { actions, elements, feedback, feedbackV2, inputsV2, overlays } from '@ecosystem/newron-design-system';
import { Notification } from '@ecosystem/newron-design-system/build/components/notifications/types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { authentication } from '../authentication';
import { SelectProps } from '@ecosystem/newron-design-system/build/components/select/Select';
import {
  AccessLevel,
  AvailableShareUser,
  AvailableShareUserGroup,
  Client,
  ShareUser,
  ShareUserGroup,
  ShareUserGroupProps,
  ShareUserProps,
} from './types';
import {
  buildDisplayName,
  buildSharedWithMessage,
  buildUserGroupDescription,
  capitalize,
  getShareEntityId,
  getWordEnding,
  mapAvailableShareUserGroupToSelectOption,
  mapAvailableShareUserToSelectOption,
  sortAvailableShareUserGroups,
  sortAvailableShareUsers,
} from './utils';
import { OwnershipTransferModal } from './OwnershipTransferModal';
import { useLogging } from '../logging';
import { clientLibComponentWrapper, getWrapperWithLogContext } from '../logging/LogProvider';

const { Modal, Toast } = overlays;
const { Button } = actions;
const { Select } = inputsV2;
const { Icon } = elements;
const { AlertSize, Tooltip } = feedback;
const { Alert } = feedbackV2;

const CSM_FAILED_SEARCH_MESSAGE =
  'No users or groups found. You may not have permission to share with this user or group.';
const CLIENT_FAILED_SEARCH_MESSAGE =
  'No users found. This person may not belong to your organization or may not use gfknewron';

const getOptionsArray = (label: string, options: { label: string; value: string; description?: string }[]) =>
  options.length ? [{ label, options }] : [];

interface ShareEntityProps {
  shareEntity: (ShareUser | ShareUserGroup) & { isUser?: boolean };
  currentUser: ShareUser;
  handleRemoveUser?: (userId: string) => void;
  handleRemoveUserGroup?: (userGroupId: number) => void;
}

const ShareEntity = ({ shareEntity, currentUser, handleRemoveUser, handleRemoveUserGroup }: ShareEntityProps) => {
  if (!shareEntity) return null;
  const user = shareEntity as ShareUser;
  const userGroup = shareEntity as ShareUserGroup;
  const isUser = shareEntity.isUser ?? Object.keys(user).includes('userId');
  const isCurrentUser = isUser && currentUser?.userId === (shareEntity as ShareUser).userId;
  const displayName = isUser ? buildDisplayName(user.givenName, user.familyName, user.email) : userGroup.userGroupName;
  let icon: React.ReactNode;
  let iconLabel: string;
  if (shareEntity.newlyAdded) {
    icon = <Icon.icons.Close />;
    iconLabel = 'Undo';
  } else if (shareEntity.newlyRemoved) {
    icon = <Icon.icons.Restore />;
    iconLabel = 'Restore';
  } else {
    icon = <Icon.icons.RemoveOutline />;
    iconLabel = 'Remove';
  }

  const removeButton: React.ReactNode = (
    <Button
      mode="simple"
      onClick={() => (isUser ? handleRemoveUser(user.userId) : handleRemoveUserGroup(userGroup.userGroupId))}
      testId="share-entity-action"
      label={iconLabel}
      disabled={user.permanent}
    >
      <Button.Icon>{icon}</Button.Icon>
    </Button>
  );
  return (
    <div className="flex gap-3 h-auto items-center overflow-hidden justify-between " data-test-id="share-entity">
      <div className="text-md text-heading flex-1 overflow-hidden">
        <div
          className={`truncate${shareEntity.newlyRemoved ? ' line-through text-inactive' : ''}`}
          data-test-id="share-entity-name"
        >
          {displayName}
          {isCurrentUser && <span className="text-inactive"> (you)</span>}
        </div>
        {displayName !== user.email && (
          <div className="text-inactive truncate" data-test-id="share-entity-description">
            {isUser ? user.email : buildUserGroupDescription(userGroup)}
          </div>
        )}
      </div>
      {user.accessLevel !== AccessLevel.Owner && (
        <div className="self-center mr-1">
          {user.permanent ? (
            <Tooltip placement="bottom">
              {removeButton}
              <Tooltip.Content>You can't remove this user.</Tooltip.Content>
            </Tooltip>
          ) : (
            removeButton
          )}
        </div>
      )}
    </div>
  );
};

export interface ShareModalProps {
  users: ShareUserProps[];
  availableUsersError?: string;
  userGroups?: ShareUserGroupProps[];
  usersError?: string;
  isOpen?: boolean;
  itemName?: string;
  itemType: string;
  sharableClients?: Client[];
  getAvailableUsers: () => Promise<AvailableShareUser[]>;
  getAvailableUserGroups?: () => Promise<AvailableShareUserGroup[]>;
  onClose: () => void;
  onShareError: (e: Error) => void;
  onGetUsersError: (e: Error) => void;
  onSave: (users: ShareUser[], userGroups?: ShareUserGroup[]) => Promise<void>;
  onSuccess?: (counts: {
    sharedWithCount: number;
    addedUsersCount: number;
    removedUsersCount: number;
    sharedWithUserGroupsCount?: number;
  }) => void;
  userMessages?: {
    ownerMessage?: React.ReactNode;
    viewersHelperMessage?: React.ReactNode;
    noViewerMessage?: React.ReactNode;
  };
  isVirtualizedList?: boolean;
  transferOwnership?: {
    onTransferOwnershipSave: (newOwner: ShareUser) => Promise<void>;
    onTransferOwnershipSuccess: (newOwner: ShareUser) => void;
    onTransferOwnershipError: (e: Error) => void;
    disableTransferButton?: boolean;
  };
  showToast?: (toast: { type: Notification; message: string | React.ReactNode }) => void;
}

const ShareModalComponent: React.FC<ShareModalProps> = ({
  isOpen,
  itemName,
  itemType,
  sharableClients,
  onClose,
  onSuccess,
  onShareError,
  onGetUsersError,
  onSave,
  getAvailableUsers,
  getAvailableUserGroups,
  availableUsersError,
  users: usersProp,
  userGroups: userGroupsProp = [],
  usersError,
  userMessages,
  isVirtualizedList = true,
  transferOwnership,
  showToast,
}) => {
  const [availableUsers, setAvailableUsers] = useState<AvailableShareUser[]>();
  const [availableUserGroups, setAvailableUserGroups] = useState<AvailableShareUserGroup[]>([]);
  const [users, setUsers] = useState<ShareUser[]>(usersProp);
  const [userGroups, setUserGroups] = useState<ShareUserGroup[]>(userGroupsProp);
  const [sharing, setSharing] = useState<boolean>(false);
  const userGroupSharingEnabled = useMemo(() => !!getAvailableUserGroups, [getAvailableUserGroups]);

  const currentUserId = useRef(authentication.getUser()?.userId).current;
  const owner = useMemo(() => usersProp.find(user => user.accessLevel === AccessLevel.Owner), [usersProp]);
  const currentUser = useMemo(() => usersProp.find(user => user.userId === currentUserId), [usersProp, currentUserId]);
  const { ownerMessage, viewersHelperMessage, noViewerMessage } = userMessages || {};

  const [toast, setToast] = useState<{ type: Notification; message: string | React.ReactNode }>();
  const handleShowToast = useCallback(
    (toast: { type: Notification; message: string | React.ReactNode }) => {
      showToast ? showToast(toast) : setToast(toast);
    },
    [showToast]
  );

  const [usersOrUGsErrorMessage, setUsersOrUGsErrorMessage] = useState<string>();

  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      getAvailableUserGroups?.()
        .then(setAvailableUserGroups)
        .catch(() => {
          setUsersOrUGsErrorMessage('We had issues loading the user groups for sharing. Please try again.');
        });
      getAvailableUsers()
        .then(setAvailableUsers)
        .catch(e => {
          setUsersOrUGsErrorMessage('We had issues loading the users for sharing. Please try again.');
          onGetUsersError(e);
        });
      availableUsersError && setUsersOrUGsErrorMessage(availableUsersError);
    }
  }, [getAvailableUsers, getAvailableUserGroups, isOpen, onGetUsersError, availableUsersError]);

  useEffect(() => {
    if (isOpen && usersProp.length > 0) {
      setUsers(usersProp);
    }
  }, [usersProp, isOpen]);

  useEffect(() => {
    if (isOpen && userGroupsProp.length > 0) {
      setUserGroups(userGroupsProp);
    }
  }, [userGroupsProp, isOpen]);

  const handleClose = useCallback(() => {
    setUsersOrUGsErrorMessage(undefined);
    setUserGroups(userGroupsProp);
    setUsers(usersProp);
    onClose();
  }, [onClose, setUsersOrUGsErrorMessage, setUsers, setUserGroups, usersProp, userGroupsProp]);

  const handleSave = useCallback(async () => {
    setSharing(true);
    try {
      const filteredUsers = users.filter(user => !user.newlyRemoved);
      const filteredUserGroups = userGroups.filter(userGroup => !userGroup.newlyRemoved);
      await onSave(filteredUsers, filteredUserGroups);
      const initialUserIds = usersProp.map(user => user.userId);
      const addedUsersCount = filteredUsers.filter(user => !initialUserIds.includes(user.userId)).length;
      const removedUsersCount = usersProp.filter(
        user => !filteredUsers.map(u => u.userId).includes(user.userId)
      ).length;
      const sharedWithCount = filteredUsers.filter(user => user.userId !== currentUserId).length;

      handleShowToast({
        type: 'success',
        message: `Changes for sharing of ${itemType} "${itemName}" saved.`,
      });

      onSuccess?.({
        sharedWithCount,
        addedUsersCount,
        removedUsersCount,
        sharedWithUserGroupsCount: userGroupSharingEnabled ? filteredUserGroups.length : undefined,
      });
      setUsers(filteredUsers);
      setUserGroups(filteredUserGroups);
      handleClose();
    } catch (e) {
      handleShowToast({
        type: 'error',
        message: `Changes for sharing of ${itemType} "${itemName}" failed. Please try again.`,
      });

      onShareError(e);
      handleClose();
    } finally {
      setSharing(false);
    }
  }, [
    users,
    onSave,
    itemName,
    onSuccess,
    handleClose,
    onShareError,
    itemType,
    usersProp,
    currentUserId,
    userGroups,
    userGroupSharingEnabled,
    handleShowToast,
  ]);

  const handleAddUserGroup = useCallback(
    (userGroupId: string) => {
      const userGroup = (availableUserGroups ?? []).find(ug => ug.userGroupId === Number(userGroupId));
      setUserGroups([
        {
          ...userGroup,
          accessLevel: AccessLevel.Viewer,
          newlyAdded: true,
        },
        ...userGroups,
      ]);
    },
    [availableUserGroups, userGroups]
  );

  const handleRemoveUserGroup = useCallback((userGroupId: number) => {
    setUserGroups(prevUserGroups =>
      prevUserGroups
        .map(userGroup => {
          if (userGroup.userGroupId === userGroupId) {
            if (userGroup.newlyAdded) {
              return;
            }
            return { ...userGroup, newlyRemoved: !userGroup.newlyRemoved };
          }
          return userGroup;
        })
        .filter(Boolean)
    );
  }, []);

  const handleAddUser = useCallback(
    (userId: string) => {
      const user = (availableUsers ?? []).find(user => user.userId === userId);
      const shareUser: ShareUser = { ...user, accessLevel: AccessLevel.Viewer, newlyAdded: true };
      setUsers(prevUsers => [prevUsers[0], shareUser, ...prevUsers.slice(1)]);
    },
    [availableUsers]
  );

  const handleRemoveUser = useCallback((userId: string) => {
    setUsers(prevUsers =>
      prevUsers
        .map(user => {
          if (user.userId === userId) {
            if (user.newlyAdded) {
              return;
            }
            return { ...user, newlyRemoved: !user.newlyRemoved };
          }
          return user;
        })
        .filter(Boolean)
    );
  }, []);

  const handleAdd = useCallback(
    (id: string) => {
      if (/^\d+$/.test(id)) {
        handleAddUserGroup(id);
      } else {
        handleAddUser(id);
      }
    },
    [handleAddUserGroup, handleAddUser]
  );

  const addedUsers = users
    .filter(user => user.userId !== currentUserId && user.newlyAdded)
    .map(user => ({ ...user, isUser: true }));
  const addedUserGroups = userGroups
    .filter(userGroup => userGroup.newlyAdded)
    .map(userGroup => ({ ...userGroup, isUser: false }));
  const addedShareEntities = [...addedUsers, ...addedUserGroups];

  const removedUsers = users.filter(user => user.newlyRemoved);
  const removedUserGroups = userGroups.filter(userGroup => userGroup.newlyRemoved);

  const filteredUsersOptions = useMemo(() => {
    return (availableUsers ?? [])
      .filter(uo => !users.find(u => uo.userId === u.userId))
      .map(mapAvailableShareUserToSelectOption);
  }, [availableUsers, users]);

  const filteredUserGroupsOptions = useMemo(() => {
    return (availableUserGroups ?? [])
      .filter(uo => !(userGroups || []).find(u => uo.userGroupId === u.userGroupId))
      .map(mapAvailableShareUserGroupToSelectOption);
  }, [availableUserGroups, userGroups]);

  const hasChanges =
    removedUsers.length > 0 || removedUserGroups.length > 0 || addedUsers.length > 0 || addedUserGroups.length > 0;

  const usersAndUserGroupsOrdered = useMemo(
    () => [
      ...userGroups
        .filter(userGroup => !userGroup.newlyAdded)
        .map(userGroup => ({ ...userGroup, isUser: false }))
        .sort((a, b) => (a.userGroupName?.toLowerCase() > b.userGroupName?.toLowerCase() ? 1 : -1)),
      ...users
        .filter(user => user.accessLevel !== AccessLevel.Owner && !user.newlyAdded)
        .map(user => ({ ...user, isUser: true }))
        .sort((a, b) =>
          buildDisplayName(a.givenName, a.familyName, a.email)?.toLowerCase() >
          buildDisplayName(b.givenName, b.familyName, b.email)?.toLowerCase()
            ? 1
            : -1
        ),
    ],
    [userGroups, users]
  );

  const clients = useMemo(
    () =>
      [...new Set(usersAndUserGroupsOrdered.map(shareEntity => shareEntity.clientId))].map(clientId => ({
        clientId,
        clientName: capitalize(
          sharableClients?.find(client => client.clientId === clientId)?.clientName ?? 'Unknown client'
        ),
      })),
    [usersAndUserGroupsOrdered, sharableClients]
  );

  const categorizeByClient = sharableClients && clients.length > 1;
  const optionsAvailable = filteredUsersOptions.length > 0 || filteredUserGroupsOptions.length > 0;

  const selectDisabled = !availableUsers || !!usersOrUGsErrorMessage || !!usersError || !optionsAvailable;
  const selectInformationMessage = userGroupSharingEnabled ? CSM_FAILED_SEARCH_MESSAGE : CLIENT_FAILED_SEARCH_MESSAGE;
  const selectPlaceholder = useMemo(() => {
    if (optionsAvailable) {
      if (!availableUsers) {
        return 'Loading users...';
      } else if (userGroupSharingEnabled) {
        return 'Find groups and individual users by name or email…';
      } else {
        return 'Find individual users by name or email…';
      }
    } else {
      return 'No more users to share with';
    }
  }, [optionsAvailable, availableUsers, userGroupSharingEnabled]);

  const selectOptions: SelectProps['options'] = useMemo(() => {
    if (userGroupSharingEnabled) {
      return sharableClients
        ?.map(client => {
          const clientUsers = (availableUsers ?? [])
            .filter(uo => !users.find(u => uo.userId === u.userId) && uo.clientId === client.clientId)
            .sort(sortAvailableShareUsers)
            .map(mapAvailableShareUserToSelectOption);
          const clientGroups = (availableUserGroups ?? [])
            .filter(
              uo => !(userGroups || []).find(u => uo.userGroupId === u.userGroupId) && uo.clientId === client.clientId
            )
            .sort(sortAvailableShareUserGroups)
            .map(mapAvailableShareUserGroupToSelectOption);
          return [
            ...getOptionsArray(`${client.clientName} ${getWordEnding(clientUsers.length, 'user')}`, clientUsers),
            ...getOptionsArray(`${client.clientName} ${getWordEnding(clientGroups.length, 'group')}`, clientGroups),
          ];
        })
        .flat();
    } else {
      return filteredUsersOptions.concat(filteredUserGroupsOptions);
    }
  }, [
    availableUserGroups,
    availableUsers,
    filteredUserGroupsOptions,
    filteredUsersOptions,
    sharableClients,
    userGroupSharingEnabled,
    userGroups,
    users,
  ]);

  const transferOwnershipButtonDisabled =
    !!usersOrUGsErrorMessage || !!usersError || transferOwnership?.disableTransferButton;

  const handleTransferOwnership = useCallback(
    async (newOwner: ShareUser) => {
      try {
        await transferOwnership.onTransferOwnershipSave(newOwner);

        handleShowToast({
          type: 'success',
          message: (
            <span data-test-id="ownership-transfer-toast">
              Ownership of <b>{itemName}</b> transferred.
            </span>
          ),
        });
        transferOwnership.onTransferOwnershipSuccess(newOwner);
        setIsTransferOwnershipModalOpen(false);

        handleClose();
      } catch (e) {
        handleShowToast({
          type: 'error',
          message: (
            <span data-test-id="ownership-transfer-error-toast">
              Transferring the ownership of {itemType} <b>{itemName}</b> failed. Please try again.
            </span>
          ),
        });

        transferOwnership.onTransferOwnershipError(e);
        setIsTransferOwnershipModalOpen(false);
        handleClose();
      }
    },
    [handleClose, transferOwnership, itemType, itemName, handleShowToast]
  );

  return (
    <>
      {!showToast && toast && (
        <Toast type={toast.type} onComplete={() => setToast(undefined)}>
          {toast.message}
        </Toast>
      )}

      {!isTransferOwnershipModalOpen && (
        <Modal onClose={handleClose} isOpen={isOpen} size="md">
          <Modal.Header>
            Share "{itemName}" {itemType}
          </Modal.Header>
          <Modal.Body overflow={true}>
            <div className="flex flex-column gap-5">
              <div data-test-id="share-owner" className="flex flex-column gap-4">
                <p className="text-sm text-heading">Owner</p>
                <div className="flex justify-between gap-2">
                  <ShareEntity shareEntity={owner} currentUser={currentUser} />
                  {!!transferOwnership && (
                    <Button
                      mode="simple"
                      onClick={() => setIsTransferOwnershipModalOpen(true)}
                      disabled={transferOwnershipButtonDisabled}
                      testId="ownership-transfer-button"
                    >
                      <Button.Icon>
                        <Icon.icons.Edit />
                      </Button.Icon>
                      Change
                    </Button>
                  )}
                </div>
                {ownerMessage && <div className="text-sm">{ownerMessage}</div>}
              </div>
              <div className="w-full bg-dark" style={{ height: '1px' }} />
              <div data-test-id="available-users">
                <Select
                  isVirtualizedList={isVirtualizedList}
                  options={selectOptions ?? []}
                  placeholder={selectPlaceholder}
                  disabled={selectDisabled}
                  informationMessage={selectInformationMessage}
                  name="share-modal-select"
                  onChange={handleAdd}
                  label="Share with"
                  combobox
                  errorMessage={usersOrUGsErrorMessage}
                />
              </div>
              {addedShareEntities.length > 0 && (
                <div
                  className="px-6 bg-brand-lightest flex flex-column gap-5 py-4"
                  style={{ margin: '0 -24px' }}
                  data-test-id="newly-added-share-entities"
                >
                  <p className="text-sm text-heading">
                    {buildSharedWithMessage(addedUsers.length, addedUserGroups.length)} will be added
                  </p>
                  <div className="pl-5 flex flex-column	gap-4">
                    {addedShareEntities.map(shareEntity => (
                      <ShareEntity
                        key={getShareEntityId(shareEntity)}
                        shareEntity={shareEntity}
                        currentUser={currentUser}
                        handleRemoveUser={handleRemoveUser}
                        handleRemoveUserGroup={handleRemoveUserGroup}
                      />
                    ))}
                  </div>
                </div>
              )}
              {!!usersError && <Alert title={usersError} type="error" size={AlertSize.SMALL} />}
              {addedShareEntities.length === 0 && usersAndUserGroupsOrdered.length === 0 && noViewerMessage}
              {usersAndUserGroupsOrdered.length > 0 && (
                <>
                  <div>
                    <div className="text-md font-bold text-heading">Who can view</div>
                    {viewersHelperMessage}
                  </div>
                  <div className="flex flex-column gap-5" data-test-id="shared-with">
                    {categorizeByClient ? (
                      clients.map(client => (
                        <div data-test-id="sharing-client" key={client.clientId}>
                          <p className="text-sm text-heading mb-4">{client.clientName}</p>
                          <div className="pl-5 flex flex-column gap-4">
                            {usersAndUserGroupsOrdered
                              .filter(sharingEntity => sharingEntity.clientId === client.clientId)
                              .map(sharingEntity => (
                                <ShareEntity
                                  key={getShareEntityId(sharingEntity)}
                                  shareEntity={sharingEntity}
                                  currentUser={currentUser}
                                  handleRemoveUser={handleRemoveUser}
                                  handleRemoveUserGroup={handleRemoveUserGroup}
                                />
                              ))}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="pl-5 flex flex-column gap-4">
                        {usersAndUserGroupsOrdered.map(sharingEntity => (
                          <ShareEntity
                            key={getShareEntityId(sharingEntity)}
                            shareEntity={sharingEntity}
                            currentUser={currentUser}
                            handleRemoveUser={handleRemoveUser}
                            handleRemoveUserGroup={handleRemoveUserGroup}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button testId="share-cancel" mode="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button testId="share-confirmation" disabled={!hasChanges || sharing} onClick={handleSave}>
              Apply changes
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {!!transferOwnership && (
        <OwnershipTransferModal
          itemType={itemType}
          isOpen={isTransferOwnershipModalOpen}
          onCancel={() => setIsTransferOwnershipModalOpen(false)}
          onConfirm={handleTransferOwnership}
          availableUsers={availableUsers}
        />
      )}
    </>
  );
};

export const ShareModal = clientLibComponentWrapper(ShareModalComponent, 'shareModal');
